import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/context/AuthContext.js';
import { BrandingProvider } from '@/context/BrandingContext.js';
import { AppShell } from '@/components/layout/AppShell.js';
import { DashboardPage } from '@/pages/DashboardPage.js';
import { MyTasksPage } from '@/pages/MyTasksPage.js';
import { ProjectsPage } from '@/pages/ProjectsPage.js';
import { ProjectDetailPage } from '@/pages/ProjectDetailPage.js';
import { TasksPage } from '@/pages/TasksPage.js';
import { TimeTrackingPage } from '@/pages/TimeTrackingPage.js';
import { TeamPage } from '@/pages/TeamPage.js';
import { ReportsPage } from '@/pages/ReportsPage.js';
import { ClientsPage } from '@/pages/ClientsPage.js';
import { LeadsPage } from '@/pages/LeadsPage.js';
import { PromotionsPage } from '@/pages/PromotionsPage.js';
import { FinancePage } from '@/pages/FinancePage.js';
import { SeoPage } from '@/pages/SeoPage.js';
import { StandupsPage } from '@/pages/StandupsPage.js';
import { BlockersPage } from '@/pages/BlockersPage.js';
import { SettingsPage } from '@/pages/SettingsPage.js';
import { McpSettingsPage } from '@/pages/McpSettingsPage.js';
import { LoginPage } from '@/pages/LoginPage.js';
import { ClientPortalPage } from '@/pages/ClientPortalPage.js';
import { NotFoundPage } from '@/pages/NotFoundPage.js';

import { ToastProvider } from '@/context/ToastContext.js';
import { ActivityLogsPage } from '@/pages/ActivityLogsPage.js';
import { RoleGuard } from '@/components/auth/RoleGuard.js';
import { UnauthorizedPage } from '@/pages/UnauthorizedPage.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        // Never retry 401 Unauthorized or 403 Forbidden in a loop
        if (error?.status === 403 || error?.status === 401) return false;
        return failureCount < 1;
      },
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HashRouter>
        <AuthProvider>
          <BrandingProvider>
            <ToastProvider>
              <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/portal" element={<ClientPortalPage />} />
            <Route path="/portal/:token" element={<ClientPortalPage />} />
            <Route path="/client-portal" element={<ClientPortalPage />} />
            <Route path="/portal/certificate/:certificateId" element={<ClientPortalPage />} />

            {/* Authenticated Application Shell */}
            <Route path="/" element={<AppShell />}>
              <Route index element={<Navigate to="/my-tasks" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="my-tasks" element={<MyTasksPage />} />
              <Route path="projects" element={<ProjectsPage />} />
              <Route path="projects/:id" element={<ProjectDetailPage />} />
              <Route path="tasks" element={<TasksPage />} />
              <Route path="tasks/:id" element={<TasksPage />} />
              <Route path="blockers" element={<BlockersPage />} />
              
              {/* Time Tracking: Owner, Manager, Developer, Team Member */}
              <Route
                path="time"
                element={
                  <RoleGuard allowedRoles={['owner', 'manager', 'developer', 'team_member']}>
                    <TimeTrackingPage />
                  </RoleGuard>
                }
              />

              {/* Team Directory: Owner, Manager, Developer, Team Member */}
              <Route
                path="team"
                element={
                  <RoleGuard allowedRoles={['owner', 'manager', 'developer', 'team_member']}>
                    <TeamPage />
                  </RoleGuard>
                }
              />
              <Route
                path="team/:id"
                element={
                  <RoleGuard allowedRoles={['owner', 'manager', 'developer', 'team_member']}>
                    <TeamPage />
                  </RoleGuard>
                }
              />

              {/* Reports: All roles (viewer is read-only) */}
              <Route path="reports" element={<ReportsPage />} />
              <Route path="reports/employees/:id" element={<ReportsPage />} />

              {/* Clients & Leads: Owner and Manager ONLY */}
              <Route
                path="clients"
                element={
                  <RoleGuard allowedRoles={['owner', 'manager']}>
                    <ClientsPage />
                  </RoleGuard>
                }
              />
              <Route
                path="leads"
                element={
                  <RoleGuard allowedRoles={['owner', 'manager']}>
                    <LeadsPage />
                  </RoleGuard>
                }
              />
              <Route
                path="promotions"
                element={
                  <RoleGuard allowedRoles={['owner', 'manager']}>
                    <PromotionsPage />
                  </RoleGuard>
                }
              />

              {/* Finance: Owner ONLY */}
              <Route
                path="finance"
                element={
                  <RoleGuard allowedRoles={['owner']}>
                    <FinancePage />
                  </RoleGuard>
                }
              />

              {/* SEO: Owner, Manager, Developer, Team Member */}
              <Route
                path="seo"
                element={
                  <RoleGuard allowedRoles={['owner', 'manager', 'developer', 'team_member']}>
                    <SeoPage />
                  </RoleGuard>
                }
              />

              {/* Standups: Owner, Manager, Developer, Team Member */}
              <Route
                path="standups"
                element={
                  <RoleGuard allowedRoles={['owner', 'manager', 'developer', 'team_member']}>
                    <StandupsPage />
                  </RoleGuard>
                }
              />

              {/* Settings & Scoped API Keys: Owner ONLY */}
              <Route
                path="settings"
                element={
                  <RoleGuard allowedRoles={['owner']}>
                    <SettingsPage />
                  </RoleGuard>
                }
              />

              {/* Dedicated MCP Tool Configuration: Owner ONLY */}
              <Route
                path="mcp-settings"
                element={
                  <RoleGuard allowedRoles={['owner']}>
                    <McpSettingsPage />
                  </RoleGuard>
                }
              />

              {/* Activity & Compliance Audit Logs: All authenticated members */}
              <Route path="activity" element={<ActivityLogsPage />} />
              <Route path="activity-logs" element={<Navigate to="/activity" replace />} />

              <Route path="unauthorized" element={<UnauthorizedPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
            </ToastProvider>
        </BrandingProvider>
      </AuthProvider>
      </HashRouter>
    </QueryClientProvider>
  );
}

export default App;
